import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Chip,
  Spinner,
  Checkbox,
  Input,
  Textarea,
  Select,
  SelectItem,
} from "@heroui/react";
import { useState, useEffect } from "react";
import { Checkmark, AlertTriangle, Edit, Save } from "assets/icons";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";

interface ContactImportModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
  contacts: any[];
}

interface CompanyMatch {
  id: number;
  name: string;
  email?: string;
  phone_direct?: string;
  website?: string;
}

export const ContactImportModal = ({
  isOpen,
  onOpenChange,
  contacts,
}: ContactImportModalProps) => {
  const { session } = useSession();
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [companyMatches, setCompanyMatches] = useState<Record<string, CompanyMatch[]>>({});
  const [selectedCompanies, setSelectedCompanies] = useState<Record<string, number | 'new'>>({});
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState<{ success: any[], errors: any[] } | null>(null);
  const [editingContact, setEditingContact] = useState<number | null>(null);
  const [editedContacts, setEditedContacts] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen && contacts.length > 0) {
      // Reset all state when modal opens with new contacts
      setImportResults(null);
      setEditingContact(null);
      setSelectedCompanies({});
      setCompanyMatches({});
      
      // Initialize edited contacts with the original data
      setEditedContacts([...contacts]);
      
      // Initialize selected contacts (all valid contacts)
      const validContacts = contacts.filter(contact => !contact.error);
      setSelectedContacts(new Set(validContacts.map((_, index) => index.toString())));
      
      // Find company matches for contacts with company names
      findCompanyMatches();
    }
  }, [isOpen, contacts]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setImportResults(null);
      setEditingContact(null);
      setSelectedCompanies({});
      setCompanyMatches({});
      setSelectedContacts(new Set());
      setEditedContacts([]);
      setIsImporting(false);
    }
  }, [isOpen]);

  const findCompanyMatches = async () => {
    const companiesToSearch = contacts
      .filter(contact => contact.companyName && !contact.error)
      .map(contact => contact.companyName);

    const uniqueCompanies = [...new Set(companiesToSearch)];
    const matches: Record<string, CompanyMatch[]> = {};

    for (const companyName of uniqueCompanies) {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/contact/search-companies?query=${encodeURIComponent(companyName)}`,
          { method: "GET" },
          session,
        );
        
        if (response.ok) {
          const companies = await response.json();
          matches[companyName] = companies;
        }
      } catch (error) {
        console.error(`Error searching for company ${companyName}:`, error);
      }
    }

    setCompanyMatches(matches);
  };

  const handleContactSelection = (index: string, isSelected: boolean) => {
    const newSelected = new Set(selectedContacts);
    if (isSelected) {
      newSelected.add(index);
    } else {
      newSelected.delete(index);
    }
    setSelectedContacts(newSelected);
  };

  const handleCompanySelection = (contactIndex: string, companyId: number | 'new') => {
    setSelectedCompanies(prev => ({
      ...prev,
      [contactIndex]: companyId
    }));
  };

  const handleEditContact = (index: number) => {
    setEditingContact(index);
  };

  const handleSaveContact = () => {
    setEditingContact(null);
    // The edited data is already in editedContacts state
  };

  const handleContactChange = (index: number, field: string, value: any) => {
    const updatedContacts = [...editedContacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value
    };

    // Re-validate the contact
    const contact = updatedContacts[index];
    
    // Clear previous errors
    delete contact.error;
    delete contact.parsedTags;
    delete contact.parsedBirthdate;

    // Validate required fields
    if (!contact.firstName || !contact.lastName) {
      contact.error = 'Voornaam en achternaam zijn verplicht';
    }

    // Parse tags
    if (contact.tags && typeof contact.tags === 'string') {
      contact.parsedTags = contact.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag);
    }

    // Parse birthdate
    if (contact.birthdate && typeof contact.birthdate === 'string') {
      try {
        contact.parsedBirthdate = new Date(contact.birthdate);
        if (isNaN(contact.parsedBirthdate.getTime())) {
          contact.error = 'Ongeldige geboortedatum format (gebruik YYYY-MM-DD)';
        }
      } catch (error) {
        contact.error = 'Ongeldige geboortedatum format (gebruik YYYY-MM-DD)';
      }
    }

    setEditedContacts(updatedContacts);
  };

  const handleImport = async () => {
    setIsImporting(true);
    try {
      const contactsToImport = editedContacts.filter((_, index) => 
        selectedContacts.has(index.toString())
      ).map((contact, index) => ({
        ...contact,
        selectedCompanyId: selectedCompanies[index.toString()]
      }));

      // First, do the regular import to get results for display
      const importResponse = await fetchWithAuth(
        `${API_URL}/contact/bulk-import`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contacts: contactsToImport }),
        },
        session,
      );

      if (importResponse.ok) {
        const results = await importResponse.json();
        setImportResults(results);
        toast.success(`${results.success.length} contacten succesvol geïmporteerd`);
        if (results.errors.length > 0) {
          toast.warning(`${results.errors.length} contacten hadden fouten`);
        }

        // Now generate and download the Excel file with contact IDs
        try {
          const excelResponse = await fetchWithAuth(
            `${API_URL}/contact/generate-import-excel`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ 
                originalContacts: editedContacts,
                importResults: results
              }),
            },
            session,
          );

          if (excelResponse.ok) {
            const blob = await excelResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Get filename from Content-Disposition header or use default
            const contentDisposition = excelResponse.headers.get('Content-Disposition');
            let filename = 'contacts_import_results.xlsx';
            if (contentDisposition) {
              const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
              }
            }
            
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success("Excel bestand met contact ID's gedownload");
          } else {
            console.warn("Could not download Excel file with results");
          }
        } catch (excelError) {
          console.error("Error downloading Excel file:", excelError);
          // Don't show error to user as the import itself was successful
        }
      } else {
        const errorData = await importResponse.json();
        toast.error(errorData.message || "Fout bij het importeren van contacten");
      }
    } catch (error) {
      console.error("Error importing contacts:", error);
      toast.error("Fout bij het importeren van contacten");
    } finally {
      setIsImporting(false);
    }
  };

  const getStatusColor = (contact: any) => {
    if (contact.error) return "danger";
    if (contact.companyName && companyMatches[contact.companyName]?.length > 0) return "warning";
    return "success";
  };

  const getStatusIcon = (contact: any) => {
    if (contact.error) return <AlertTriangle className="h-4 w-4" />;
    if (contact.companyName && companyMatches[contact.companyName]?.length > 0) return <AlertTriangle className="h-4 w-4" />;
    return <Checkmark className="h-4 w-4" />;
  };

  const renderCompanySelection = (contact: any, index: number) => {
    if (!contact.companyName) return null;

    const matches = companyMatches[contact.companyName] || [];
    const contactIndex = index.toString();

    return (
      <div className="mt-2">
        <p className="text-sm font-medium text-neutral-primary mb-2">
          Bedrijf "{contact.companyName}":
        </p>
        <div className="space-y-2">
          {matches.length > 0 && (
            <div>
              <p className="text-xs text-neutral-secondary-mid mb-1">Gevonden bedrijven:</p>
              {matches.map((company) => (
                <div key={company.id} className="flex items-center gap-2 p-2 border rounded-sm">
                  <Checkbox
                    isSelected={selectedCompanies[contactIndex] === company.id}
                    onValueChange={() => handleCompanySelection(contactIndex, company.id)}
                  />
                  <div>
                    <p className="font-medium">{company.name}</p>
                    {company.email && <p className="text-xs text-neutral-secondary-mid">{company.email}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 p-2 border rounded-sm">
            <Checkbox
              isSelected={selectedCompanies[contactIndex] === 'new'}
              onValueChange={() => handleCompanySelection(contactIndex, 'new')}
            />
            <div>
              <p className="font-medium">Nieuw bedrijf aanmaken</p>
              <p className="text-xs text-neutral-secondary-mid">Bedrijf "{contact.companyName}" wordt aangemaakt</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContactFields = (contact: any, index: number) => {
    const isEditing = editingContact === index;

    if (isEditing) {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Voornaam*"
              value={contact.firstName}
              onChange={(e) => handleContactChange(index, 'firstName', e.target.value)}
              isInvalid={!contact.firstName}
              errorMessage={!contact.firstName ? "Verplicht" : ""}
            />
            <Input
              label="Achternaam*"
              value={contact.lastName}
              onChange={(e) => handleContactChange(index, 'lastName', e.target.value)}
              isInvalid={!contact.lastName}
              errorMessage={!contact.lastName ? "Verplicht" : ""}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="E-mail"
              value={contact.email}
              onChange={(e) => handleContactChange(index, 'email', e.target.value)}
            />
            <Input
              label="Mobiel telefoon"
              value={contact.phone_mobile}
              onChange={(e) => handleContactChange(index, 'phone_mobile', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Direct telefoon"
              value={contact.phone_direct}
              onChange={(e) => handleContactChange(index, 'phone_direct', e.target.value)}
            />
            <Input
              label="Functietitel"
              value={contact.jobTitle}
              onChange={(e) => handleContactChange(index, 'jobTitle', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Bedrijfsnaam"
              value={contact.companyName}
              onChange={(e) => handleContactChange(index, 'companyName', e.target.value)}
            />
            <Input
              label="Bedrijf e-mail"
              value={contact.companyEmail}
              onChange={(e) => handleContactChange(index, 'companyEmail', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Bedrijf telefoon"
              value={contact.companyPhone}
              onChange={(e) => handleContactChange(index, 'companyPhone', e.target.value)}
            />
            <Input
              label="Bedrijf website"
              value={contact.companyWebsite}
              onChange={(e) => handleContactChange(index, 'companyWebsite', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Bedrijf BTW-nummer"
              value={contact.companyVatNumber}
              onChange={(e) => handleContactChange(index, 'companyVatNumber', e.target.value)}
            />
            <Input
              label="Adres"
              value={contact.address}
              onChange={(e) => handleContactChange(index, 'address', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Tags (komma-gescheiden)"
              value={contact.tags}
              onChange={(e) => handleContactChange(index, 'tags', e.target.value)}
            />
            <Select
              label="Taal"
              value={contact.language}
              onChange={(e) => handleContactChange(index, 'language', e.target.value)}
            >
              <SelectItem key="nl_BE">Nederlands</SelectItem>
              <SelectItem key="en_US">Engels</SelectItem>
              <SelectItem key="fr_BE">Frans</SelectItem>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Geboortedatum (YYYY-MM-DD)"
              value={contact.birthdate}
              onChange={(e) => handleContactChange(index, 'birthdate', e.target.value)}
            />
            <Input
              label="Geboorteplaats"
              value={contact.birthplace}
              onChange={(e) => handleContactChange(index, 'birthplace', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Nationaliteit"
              value={contact.nationality}
              onChange={(e) => handleContactChange(index, 'nationality', e.target.value)}
            />
            <Select
              label="Geslacht"
              value={contact.gender}
              onChange={(e) => handleContactChange(index, 'gender', e.target.value)}
            >
              <SelectItem key="M">Man</SelectItem>
              <SelectItem key="V">Vrouw</SelectItem>
            </Select>
          </div>
          <Input
            label="LinkedIn"
            value={contact.linkedIn}
            onChange={(e) => handleContactChange(index, 'linkedIn', e.target.value)}
          />
          <Textarea
            label="Beschrijving"
            value={contact.description}
            onChange={(e) => handleContactChange(index, 'description', e.target.value)}
          />
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p><strong>E-mail:</strong> {contact.email || '-'}</p>
          <p><strong>Mobiel:</strong> {contact.phone_mobile || '-'}</p>
          <p><strong>Direct:</strong> {contact.phone_direct || '-'}</p>
          <p><strong>Functie:</strong> {contact.jobTitle || '-'}</p>
        </div>
        <div>
          <p><strong>Bedrijf:</strong> {contact.companyName || '-'}</p>
          <p><strong>Adres:</strong> {contact.address || '-'}</p>
          <p><strong>Tags:</strong> {contact.parsedTags?.join(', ') || '-'}</p>
        </div>
      </div>
    );
  };

  if (importResults) {
    return (
      <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="4xl">
        <ModalContent>
          <ModalHeader>Import Resultaten</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              {importResults.success.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-status-good mb-2">
                    Succesvol geïmporteerd ({importResults.success.length})
                  </h3>
                  <div className="max-h-60 overflow-y-auto">
                    {importResults.success.map((result, index) => (
                      <div key={index} className="p-2 border border-status-good/20 rounded-sm mb-2">
                        <p className="font-medium">{result.firstName} {result.lastName}</p>
                        {result.savedContact?.company && (
                          <p className="text-sm text-neutral-secondary-mid">
                            Bedrijf: {result.savedContact.company.name}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importResults.errors.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-status-bad mb-2">
                    Fouten ({importResults.errors.length})
                  </h3>
                  <div className="max-h-60 overflow-y-auto">
                    {importResults.errors.map((error, index) => (
                      <div key={index} className="p-2 border border-status-bad/20 rounded-sm mb-2">
                        <p className="font-medium">{error.firstName} {error.lastName}</p>
                        <p className="text-sm text-status-bad">{error.error}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button 
              color="secondary" 
              variant="flat"
              onPress={async () => {
                try {
                  const excelResponse = await fetchWithAuth(
                    `${API_URL}/contact/generate-import-excel`,
                    {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ 
                        originalContacts: contacts,
                        importResults: importResults
                      }),
                    },
                    session,
                  );

                  if (excelResponse.ok) {
                    const blob = await excelResponse.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    
                    const contentDisposition = excelResponse.headers.get('Content-Disposition');
                    let filename = 'contacts_import_results.xlsx';
                    if (contentDisposition) {
                      const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                      if (matches != null && matches[1]) {
                        filename = matches[1].replace(/['"]/g, '');
                      }
                    }
                    
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    toast.success("Excel bestand gedownload");
                  } else {
                    toast.error("Fout bij downloaden van Excel bestand");
                  }
                } catch (error) {
                  console.error("Error downloading Excel file:", error);
                  toast.error("Fout bij downloaden van Excel bestand");
                }
              }}
            >
              Download Excel met Contact ID's
            </Button>
            <Button color="primary" onPress={onOpenChange}>
              Sluiten
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="4xl">
      <ModalContent>
        <ModalHeader>Contacten Importeren</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <p className="text-sm text-neutral-secondary-mid">
              Controleer en bewerk de contacten voordat je ze importeert. 
              Voor contacten met bedrijfsnamen kun je kiezen tussen bestaande bedrijven of nieuwe bedrijven aanmaken.
            </p>

            <div className="max-h-96 overflow-y-auto">
              {editedContacts.map((contact, index) => (
                <div key={index} className="border rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      isSelected={selectedContacts.has(index.toString())}
                      onValueChange={(isSelected) => handleContactSelection(index.toString(), isSelected)}
                      isDisabled={!!contact.error}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusIcon(contact)}
                        <h3 className="font-medium">
                          {contact.firstName} {contact.lastName}
                        </h3>
                        <Chip
                          size="sm"
                          color={getStatusColor(contact)}
                          variant="flat"
                        >
                          {contact.error ? 'Fout' : contact.companyName ? 'Bedrijf gevonden' : 'Klaar'}
                        </Chip>
                        <Button
                          size="sm"
                          variant="light"
                          onPress={() => editingContact === index ? handleSaveContact() : handleEditContact(index)}
                          startContent={editingContact === index ? <Save /> : <Edit />}
                        >
                          {editingContact === index ? 'Opslaan' : 'Bewerken'}
                        </Button>
                      </div>

                      {renderContactFields(contact, index)}

                      {contact.error && (
                        <p className="text-status-bad text-sm mt-2">{contact.error}</p>
                      )}

                      {!editingContact && renderCompanySelection(contact, index)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="light" onPress={onOpenChange}>
            Annuleren
          </Button>
          <Button
            color="primary"
            onPress={handleImport}
            isDisabled={isImporting || selectedContacts.size === 0}
            startContent={isImporting ? <Spinner size="sm" /> : <Checkmark />}
          >
            {isImporting ? "Importeren..." : `Importeren (${selectedContacts.size})`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}; 